import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { useNotificationStore, type NotificationAction } from '../../stores/notificationStore';

/**
 * Toast surface. Renders every queued notification stacked
 * vertically in the top-right so nothing hides behind the
 * newest one. Persistent toasts (`duration <= 0`) stay open
 * until dismissed explicitly; the rest auto-dismiss via a
 * per-item timeout tied to their lifetime.
 *
 * Historical: this used to render only `notifications[0]`
 * inside an MUI `<Snackbar>`, so a persistent processing toast
 * would block every subsequent transient toast. The stack model
 * makes cascading behavior explicit — a new toast appears BELOW
 * the older ones instead of replacing them.
 *
 * Mounted inside the router (App.tsx) so an action can be a route (`to`), e.g.
 * "Open Print Queue" on every report-queued toast.
 */
export default function NotificationToast() {
  const notifications = useNotificationStore((s) => s.notifications);
  const dismissNotification = useNotificationStore((s) => s.dismissNotification);

  if (notifications.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 2000,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        maxWidth: 'min(480px, calc(100vw - 32px))',
        // Column reverse would push newest to top; keep natural
        // order so the FIRST-queued (usually the processing
        // toast) stays visually anchored at the top and later
        // ephemerals cascade below it.
        pointerEvents: 'none',
      }}
    >
      {notifications.map((n) => (
        <ToastItem key={n.id} notification={n} onDismiss={() => dismissNotification(n.id)} />
      ))}
    </Box>
  );
}

interface Notif {
  id: string;
  message: string;
  type: 'error' | 'warning' | 'info' | 'success';
  timestamp: number;
  action?: NotificationAction;
  duration?: number;
  showSpinner?: boolean;
}

function ToastItem({ notification, onDismiss }: { notification: Notif; onDismiss: () => void }) {
  const navigate = useNavigate();
  const isPersistent = (notification.duration ?? 6000) <= 0;

  // Auto-dismiss after `duration` ms. Persistent toasts skip the
  // timer entirely and stay until a caller invokes
  // dismissNotification(id) or the user clicks the close X.
  useEffect(() => {
    if (isPersistent) return;
    const ms = notification.duration ?? 6000;
    const t = setTimeout(onDismiss, ms);
    return () => clearTimeout(t);
    // The onDismiss identity changes each render (bound to id),
    // but the store's dismissNotification is stable — the wrapper
    // closure closes over the id, so the effect only needs to
    // re-fire when the notification itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification.id, notification.duration, isPersistent]);

  return (
    <Alert
      severity={notification.type}
      variant="filled"
      sx={{ pointerEvents: 'auto', boxShadow: 3, alignItems: 'center' }}
      action={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {notification.action && (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const { handler, to } = notification.action!;
                handler?.();
                if (to) navigate(to);
                onDismiss();
              }}
              sx={{ fontWeight: 700, minWidth: 'auto' }}
            >
              {notification.action.label}
            </Button>
          )}
          <IconButton
            size="small"
            color="inherit"
            aria-label="close"
            onClick={onDismiss}
            sx={{ p: 0.25 }}
          >
            <i className="bi bi-x-lg" style={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      }
    >
      {notification.showSpinner ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} color="inherit" thickness={5} />
          <span>{notification.message}</span>
        </Box>
      ) : (
        notification.message
      )}
    </Alert>
  );
}
